import {
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  Grid,
  HStack,
  Heading,
  Icon,
  Portal,
  Separator,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { LuCircleAlert, LuChevronLeft } from 'react-icons/lu'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  getDemand,
  getInterestedSibs,
  getMatchingSibs,
  publishToAllMatchingSibs,
  publishToSelectedSibs,
  confirmInterestedSibs,
  updateDemand,
  deleteDemand,
  archiveDemand,
  rejectInterestedSibs,
  markDemandNotMatchable,
} from '../api'
import { ErrorState } from '../components/ErrorState'
import { LoadingState } from '../components/LoadingState'
import {
  getTopicLabel,
  type CreateDemandPayload,
  type Demand,
  type DemandAssignment,
  type InterestedSib,
  type MatchingSib,
} from '../types'
import { InterestedSibCard } from '../components/InterestedSibCard'
import { getUnionDemandStatusTone } from '../unionDemandStatus'
import { AssignDemandDrawer } from '../components/AssignDemandDrawer'
import { onSessionReset } from '@/api/sessionScope'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import { Tooltip } from '@/components/ui/tooltip'
import { CreateDemandDrawer } from '../components/CreateDemandDrawer'
import { groupSibsByRegion } from '../regionGrouping'
import { UNION_MANAGER_ROUTES } from '@/constants/unionRoutes.constants'
import { formatCapacity } from '@/helper/valueChange.helper'

function getDemandName(demand: Demand) {
  return demand.customerName || demand.bankName || demand.serviceRecipient || '—'
}

// Draft award assignments are staged per demand in sessionStorage before the
// confirmation is sent. They belong to the union manager who staged them, so
// they are dropped on any authentication transition (SIB-924).
const ASSIGNMENT_STORAGE_PREFIX = 'marketplace-demand-assignments:'

function assignmentStorageKey(demandId: string | number) {
  return `${ASSIGNMENT_STORAGE_PREFIX}${demandId}`
}

onSessionReset(() => {
  try {
    const stale: string[] = []
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (key?.startsWith(ASSIGNMENT_STORAGE_PREFIX)) stale.push(key)
    }
    stale.forEach(key => sessionStorage.removeItem(key))
  } catch {
    // Storage access can fail (privacy mode); nothing sensitive remains in memory.
  }
})

/** SIB-924, decided option 3 (Luca, 01.09.2026): a demand is locked for
 * editing as soon as ANY interest record exists — the backend checks record
 * existence, not current status, because declines can overwrite formerly
 * interested records. `hasInterestHistory` is the authoritative signal; the
 * fallback covers responses from an older backend, where only current
 * counts and awards are visible and the backend still rejects every
 * published-demand edit anyway. */
function hasInterestEvidence(demand: Demand, interestedSibs: InterestedSib[]) {
  if (typeof demand.hasInterestHistory === 'boolean') {
    return demand.hasInterestHistory
  }
  return (
    (demand.interestsCount ?? 0) > 0 ||
    (demand.rejectedCount ?? 0) > 0 ||
    (demand.allocationsCount ?? 0) > 0 ||
    (demand.allocations?.length ?? 0) > 0 ||
    interestedSibs.length > 0
  )
}

function formatGermanDate(date?: string) {
  if (!date) return '—'

  return new Intl.DateTimeFormat('de-DE').format(new Date(date))
}

function getBucketValue(demand: Demand, serviceType: string) {
  return demand.buckets?.find(bucket => bucket.serviceType === serviceType)?.requestedUnits ?? 0
}

function getAssignedUnits(assignments: DemandAssignment[], serviceType: string) {
  return assignments.reduce((sum, assignment) => {
    const item = assignment.units.find(unit => unit.serviceType === serviceType)
    return sum + (item?.assignedUnits ?? 0)
  }, 0)
}

function getAssignedSibCount(assignments: DemandAssignment[]) {
  return assignments.length
}

function getStatusLabel(status?: string) {
  switch (status) {
    case 'PARTIALLY_COVERED':
    case 'partially_confirmed':
      return 'Teilweise vermittelt'

    case 'COVERED':
    case 'confirmed':
      return 'Vollständig vermittelt'

    case 'CLOSED':
    case 'closed':
      return 'Geschlossen'

    default:
      return 'Offen'
  }
}

function getAllocationUnits(
  allocation: NonNullable<Demand['allocations']>[number],
  serviceType: string
) {
  return (
    allocation.allocationBuckets.find(bucket => bucket.serviceType === serviceType)
      ?.allocatedUnits ?? 0
  )
}

function getRemainingUnitsFromAssignments(
  demand: Demand,
  assignments: DemandAssignment[],
  serviceType: string
) {
  const requested = getBucketValue(demand, serviceType)
  const assigned = getAssignedUnits(assignments, serviceType)

  return Math.max(requested - assigned, 0)
}

function hasAnyAssignment(assignments: DemandAssignment[]) {
  return assignments.some(assignment => assignment.units.some(unit => unit.assignedUnits > 0))
}

function hasRemainingDemand(demand: Demand, assignments: DemandAssignment[]) {
  return ['TOJ', 'CONSULTING', 'TRAINING', 'PILOT'].some(
    serviceType => getRemainingUnitsFromAssignments(demand, assignments, serviceType) > 0
  )
}

// function handleEditDemand() {
//   navigate(`/union/demands/${demand?.id}/edit`)
// }

function statusBadge(demand: Demand) {
  const tone = getUnionDemandStatusTone(demand)

  return <Badge colorPalette={tone.colorPalette}>{tone.label}</Badge>
}

function CapacityNumber({
  value,
  label,
  warning,
}: {
  value: string
  label: string
  warning?: 'yellow' | 'red'
}) {
  return (
    <Stack gap={1}>
      <HStack>
        <Text fontWeight="bold">{value}</Text>
        <CapacityWarning level={warning} />
      </HStack>

      <Text color="fg.muted" fontSize="sm">
        {label}
      </Text>
    </Stack>
  )
}

function CapacityWarning({ level }: { level?: 'yellow' | 'red' }) {
  if (!level) return null

  return (
    <Badge colorPalette={level} variant="subtle" size="xs" borderRadius="full" px={1}>
      <Icon as={LuCircleAlert} boxSize={2.5} />
    </Badge>
  )
}

function getRequestedUnits(demand: Demand, serviceType: string) {
  return demand.buckets?.find(bucket => bucket.serviceType === serviceType)?.requestedUnits ?? 0
}

function getRemainingUnits(sib: MatchingSib, serviceType: string) {
  return sib.remainingCapacity?.find(item => item.serviceType === serviceType)?.units ?? 0
}

function getRemainingDemandUnits(demand: Demand, serviceType: string) {
  return (
    demand.remainingDemandBuckets?.find(bucket => bucket.serviceType === serviceType)
      ?.remainingUnits ??
    demand.buckets?.find(bucket => bucket.serviceType === serviceType)?.requestedUnits ??
    0
  )
}

function getDemandBucketUnits(demand: Demand, serviceType: string) {
  return demand.buckets?.find(bucket => bucket.serviceType === serviceType)?.requestedUnits ?? 0
}

function getAllocatedDemandUnits(demand: Demand, serviceType: string) {
  return Math.max(
    getDemandBucketUnits(demand, serviceType) - getRemainingDemandUnits(demand, serviceType),
    0
  )
}

function getCapacityWarning(requested: number, remaining: number): 'yellow' | 'red' | undefined {
  if (requested <= 0) {
    return undefined
  }

  if (remaining <= 0) {
    return 'red'
  }

  const ratio = requested / remaining

  if (ratio > 1.05) return 'red'
  if (ratio >= 1) return 'yellow'

  return undefined
}

function getOrderedUnits(sib: MatchingSib, serviceType: string) {
  return (
    sib.orderedUnits?.find(item => item.serviceType === serviceType) ?? {
      ordered: 0,
      completed: 0,
    }
  )
}

function formatWorkingArea(value?: string) {
  if (!value) return []

  const labels: Record<string, string> = {
    CITY_RADIUS: 'Stadt + Radius',
    FEDERAL_STATE: 'Bundesland',
    GERMANY: 'Deutschland',
    AUSTRIA: 'Österreich',
    SWITZERLAND: 'Schweiz',
  }

  return value
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
    .map(item => labels[item] ?? item)
}

export function MatchingSibCard({
  sib,
  demand,
  checked,
  onChange,
}: {
  sib: MatchingSib
  demand: Demand
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  const tojRequested = getRequestedUnits(demand, 'TOJ')
  const tojRemaining = getRemainingUnits(sib, 'TOJ')

  const consultingRequested = getRequestedUnits(demand, 'CONSULTING')
  const consultingRemaining = getRemainingUnits(sib, 'CONSULTING')

  const trainingRequested = getRequestedUnits(demand, 'TRAINING')
  const trainingRemaining = getRemainingUnits(sib, 'TRAINING')

  const pilotRequested = getRequestedUnits(demand, 'PILOT')
  const pilotRemaining = getRemainingUnits(sib, 'PILOT')

  const orderedToj = getOrderedUnits(sib, 'TOJ')
  const orderedConsulting = getOrderedUnits(sib, 'CONSULTING')
  const orderedTraining = getOrderedUnits(sib, 'TRAINING')
  const orderedPilot = getOrderedUnits(sib, 'PILOT')

  const colorPalette =
    sib.trafficLightStatus === 'green'
      ? 'green'
      : sib.trafficLightStatus === 'yellow'
        ? 'yellow'
        : 'red'

  const workingAreaLabels = formatWorkingArea(sib.workingArea)

  const demandTopics = new Set(
    (demand.topic ?? '')
      .split(',')
      .map(topic => topic.trim())
      .filter(Boolean)
  )

  return (
    <Card.Root
      variant="outline"
      borderColor={checked ? 'blue.500' : 'border'}
      bg="bg"
      cursor="pointer"
      onClick={() => onChange(!checked)}
    >
      <Card.Body>
        <Stack>
          <HStack justify="space-between" align="start">
            <HStack>
              <Checkbox.Root
                checked={checked}
                onClick={event => event.stopPropagation()}
                onCheckedChange={details => onChange(Boolean(details.checked))}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control />
              </Checkbox.Root>
              <Text fontWeight="bold">{sib.name}</Text>
            </HStack>

            <Stack align="end" gap={1}>
              <Badge colorPalette={colorPalette} variant="subtle">
                {sib.postalCode} {sib.city}
              </Badge>

              {workingAreaLabels.length > 0 && (
                <HStack wrap="wrap" justify="end" gap={1}>
                  {workingAreaLabels.map(area => (
                    <Badge key={area} colorPalette={colorPalette} variant="subtle" size="sm">
                      {area}
                    </Badge>
                  ))}
                </HStack>
              )}
            </Stack>
          </HStack>

          <Stack gap={2}>
            <Text color="fg.muted" fontSize="sm">
              Restkapazität
            </Text>

            <SimpleGrid columns={4}>
              <CapacityNumber
                value={`${formatCapacity(tojRemaining)} Stk.`}
                label="TOJ"
                warning={getCapacityWarning(tojRequested, tojRemaining)}
              />

              <CapacityNumber
                value={`${formatCapacity(consultingRemaining)} St.`}
                label="Beratung"
                warning={getCapacityWarning(consultingRequested, consultingRemaining)}
              />

              <CapacityNumber
                value={`${formatCapacity(trainingRemaining)} T.`}
                label="Training"
                warning={getCapacityWarning(trainingRequested, trainingRemaining)}
              />

              <CapacityNumber
                value={`${formatCapacity(pilotRemaining)} Stk.`}
                label="Pilot"
                warning={getCapacityWarning(pilotRequested, pilotRemaining)}
              />
            </SimpleGrid>
          </Stack>

          <Stack gap={2}>
            <Text color="fg.muted" fontSize="sm">
              Bestellt / Durchgeführt
            </Text>

            <SimpleGrid columns={4}>
              <CapacityNumber
                value={`${orderedToj.ordered} / ${formatCapacity(orderedToj.completed)}`}
                label="TOJ"
              />
              <CapacityNumber
                value={`${orderedConsulting.ordered} / ${formatCapacity(orderedConsulting.completed)}`}
                label="Beratung"
              />
              <CapacityNumber
                value={`${orderedTraining.ordered} / ${formatCapacity(orderedTraining.completed)}`}
                label="Training"
              />
              <CapacityNumber
                value={`${orderedPilot.ordered} / ${formatCapacity(orderedPilot.completed)}`}
                label="Pilot"
              />
            </SimpleGrid>
          </Stack>

          <Stack gap={2}>
            <Text color="fg.muted" fontSize="sm">
              Themen
            </Text>

            <HStack wrap="wrap">
              {sib.topics.length > 0 ? (
                <HStack wrap="wrap" gap={1} mt={1}>
                  {sib.topics.map(topic => {
                    const matches = demandTopics.has(topic.code)

                    return (
                      <Badge
                        key={topic.code}
                        colorPalette={matches ? 'green' : 'red'}
                        variant="subtle"
                        size="sm"
                      >
                        {topic.nameDe}
                      </Badge>
                    )
                  })}
                </HStack>
              ) : (
                <Text color="fg.muted">—</Text>
              )}
            </HStack>

            {(sib.missingTopics ?? []).length > 0 && (
              <HStack wrap="wrap">
                <Text color="fg.muted" fontSize="sm">
                  Fehlend
                </Text>

                {sib.missingTopics?.map(topic => (
                  <Badge key={topic} colorPalette="red">
                    {topic}
                  </Badge>
                ))}
              </HStack>
            )}
          </Stack>
        </Stack>
      </Card.Body>
    </Card.Root>
  )
}

export function UnionDemandDetailPage() {
  const { demandId } = useParams<{ demandId: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  const [demand, setDemand] = useState<Demand | null>(null)
  const [matchingSibs, setMatchingSibs] = useState<MatchingSib[]>([])
  const [selectedSibIds, setSelectedSibIds] = useState<string[]>([])
  const regionGroups = useMemo(() => groupSibsByRegion(matchingSibs), [matchingSibs])
  const [isLoading, setIsLoading] = useState(true)
  const [isPublishingSelected, setIsPublishingSelected] = useState(false)
  const [isPublishingAll, setIsPublishingAll] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [interestedSibs, setInterestedSibs] = useState<InterestedSib[]>([])
  const [selectedInterestedSibIds, setSelectedInterestedSibIds] = useState<string[]>([])
  const [showAssignDrawer, setShowAssignDrawer] = useState(false)
  const [isAssigning, setIsAssigning] = useState(false)
  const [isRejectingInterested, setIsRejectingInterested] = useState(false)
  const [isMarkingNotMatchable, setIsMarkingNotMatchable] = useState(false)
  const [activeTab, setActiveTab] = useState('matching')
  const [assignments, setAssignments] = useState<DemandAssignment[]>([])

  const [showEditDrawer, setShowEditDrawer] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)

  async function handleUpdateDemand(payload: CreateDemandPayload) {
    if (!demand || isUpdating) return

    try {
      setIsUpdating(true)

      await updateDemand({
        ...demand,
        customerId: payload.customerId,
        buckets: payload.buckets,
        periodStart: payload.periodStart,
        periodEnd: payload.periodEnd,
        postal: payload.postal,
        city: payload.city,
        country: payload.country,
        salesRegion: payload.salesRegion,
        topic: payload.topic,
        description: payload.description,
      })

      toast.success('Bedarf wurde aktualisiert.')
      setShowEditDrawer(false)
      await loadPage()
    } catch (e) {
      const err = e as { status?: number; message?: string }
      // Any 400 means the server rejected this save against its CURRENT state
      // — in practice a stale form (the demand gained an interest, was
      // published or archived after the drawer opened; the backend's
      // validation code INVALID_PAYLOAD is shared by all of these, and its
      // message text is not a stable contract). The edit cannot succeed by
      // retrying, so close the form, reload eligibility and say why. Other
      // failures (network, 5xx) keep the drawer open so the input survives.
      const editingNoLongerPossible = err?.status === 400

      if (editingNoLongerPossible) {
        toast.error(
          'Der Bedarf kann nicht mehr bearbeitet werden — der Stand wurde inzwischen geändert. Die Ansicht wurde aktualisiert.'
        )
        setShowEditDrawer(false)
        await loadPage()
      } else {
        toast.error(err?.message || 'Bedarf konnte nicht aktualisiert werden.')
      }
    } finally {
      setIsUpdating(false)
    }
  }

  // Confirmation + duplicate-request guard for the destructive actions (SIB-924).
  const [confirmAction, setConfirmAction] = useState<'delete' | 'archive' | null>(null)
  const [isRemoving, setIsRemoving] = useState(false)

  async function handleDeleteDemand() {
    if (!demand || isRemoving) return

    try {
      setIsRemoving(true)
      await deleteDemand(demand.id)
      toast.success('Bedarf wurde gelöscht.')
      navigate(UNION_MANAGER_ROUTES.marketplace, { replace: true })
    } catch {
      toast.error('Bedarf konnte nicht gelöscht werden.')
    } finally {
      setIsRemoving(false)
      setConfirmAction(null)
    }
  }

  async function handleArchiveDemand() {
    if (!demand || isRemoving) return

    try {
      setIsRemoving(true)
      await archiveDemand(demand.id)

      toast.success('Bedarf wurde archiviert.')

      navigate(UNION_MANAGER_ROUTES.marketplace, { replace: true })
    } catch {
      toast.error('Bedarf konnte nicht archiviert werden.')
    } finally {
      setIsRemoving(false)
      setConfirmAction(null)
    }
  }

  async function loadPage() {
    if (!demandId) return

    try {
      setIsLoading(true)
      setError(null)

      const [demandResult, sibsResult, interestedResult] = await Promise.all([
        getDemand(demandId),
        getMatchingSibs(demandId),
        getInterestedSibs(demandId),
      ])

      setDemand(demandResult)
      if (demandId) {
        const storedAssignments = sessionStorage.getItem(assignmentStorageKey(demandId))

        if (storedAssignments) {
          setAssignments(JSON.parse(storedAssignments) as DemandAssignment[])
        }
      }

      setActiveTab(getInitialTab(demandResult.status, demandResult.interestsCount ?? 0))
      setMatchingSibs(sibsResult)
      setInterestedSibs(Array.isArray(interestedResult) ? interestedResult : [])
    } catch {
      setError('Could not load demand details.')
    } finally {
      setIsLoading(false)
    }
  }

  function toggleInterestedSib(sibId: string, checked: boolean) {
    setSelectedInterestedSibIds(current =>
      checked ? [...current, sibId] : current.filter(id => id !== sibId)
    )
  }

  async function handlePublishSelected() {
    if (!demand || selectedSibIds.length === 0) return

    try {
      setIsPublishingSelected(true)
      await publishToSelectedSibs(demand, selectedSibIds)

      toast.success('Bedarf wurde an die ausgewählten SIB ausgeschrieben.')

      navigate(UNION_MANAGER_ROUTES.marketplace, { replace: true })
    } finally {
      setIsPublishingSelected(false)
    }
  }

  async function handlePublishAll() {
    if (!demand) return

    try {
      setIsPublishingAll(true)
      await publishToAllMatchingSibs(demand)

      toast.success('Bedarf wurde an alle passenden SIB ausgeschrieben.')

      navigate(UNION_MANAGER_ROUTES.marketplace, { replace: true })
    } finally {
      setIsPublishingAll(false)
    }
  }

  function isExpiredWithoutAssignment(demand: Demand) {
    if (!demand.periodEnd) return false

    const today = new Date()
    const end = new Date(demand.periodEnd)

    today.setHours(0, 0, 0, 0)
    end.setHours(0, 0, 0, 0)

    return end < today && (demand.allocationsCount ?? 0) === 0
  }

  function toggleSib(sibId: string, checked: boolean) {
    setSelectedSibIds(current =>
      checked ? [...current, sibId] : current.filter(id => id !== sibId)
    )
  }

  async function handleAssignDemand(
    assignmentItems: Array<{
      sibId: string
      serviceType: string
      units: number
    }>
  ) {
    if (!demand) return
    try {
      setIsAssigning(true)

      await confirmInterestedSibs(demand.id, assignmentItems)

      await loadPage()

      setSelectedInterestedSibIds([])
      setShowAssignDrawer(false)

      return
    } finally {
      setIsAssigning(false)
    }
  }

  async function handleMarkNotMatchable() {
    if (!demand) return

    try {
      setIsMarkingNotMatchable(true)

      await markDemandNotMatchable(demand.id)

      toast.success('Bedarf wurde als nicht vermittelbar markiert.')

      await loadPage()

      setActiveTab('retro')
    } catch {
      toast.error('Bedarf konnte nicht als nicht vermittelbar markiert werden.')
    } finally {
      setIsMarkingNotMatchable(false)
    }
  }

  async function handleRejectSelectedInterestedSibs() {
    if (!demand || selectedInterestedSibIds.length === 0) return

    try {
      setIsRejectingInterested(true)

      await rejectInterestedSibs(demand.id, selectedInterestedSibIds)

      toast.success('Die ausgewählten SIB wurden abgelehnt.')

      setSelectedInterestedSibIds([])

      await loadPage()
    } catch {
      toast.error('Die ausgewählten SIB konnten nicht abgelehnt werden.')
    } finally {
      setIsRejectingInterested(false)
    }
  }

  function isDemandActionable(status?: string) {
    return (
      status === 'OFFERED' ||
      status === 'INTERESTED_AVAILABLE' ||
      status === 'PARTIALLY_COVERED' ||
      status === 'open' ||
      status === 'published' ||
      status === 'partially_confirmed'
    )
  }

  useEffect(() => {
    void loadPage()
  }, [demandId])

  if (isLoading) {
    return <LoadingState message="Bedarf wird geladen..." />
  }

  if (error || !demand) {
    return <ErrorState message={error ?? 'Bedarf nicht gefunden.'} onRetry={loadPage} />
  }

  function getInitialTab(status?: string, interestsCount = 0) {
    if (
      status === 'PARTIALLY_COVERED' ||
      status === 'COVERED' ||
      status === 'partially_confirmed' ||
      status === 'confirmed' ||
      status === 'not_matchable_remainder'
    ) {
      return 'retro'
    }
    if (
      (status === 'INTERESTED_AVAILABLE' || status === 'INTERESTED_SIBS_AVAILABLE') &&
      (demand?.interestsCount ?? 0) > 0
    ) {
      return 'interested'
    }

    return 'matching'
  }

  const hasInterestedSibs = (demand?.interestsCount ?? 0) > 0
  const isPublished = demand?.published === true

  // Mirrors the backend guards (SIB-881): delete requires status OFFERED or
  // INTERESTED_AVAILABLE, archive only requires the demand not to be archived yet.
  // The expired-without-assignment case keeps its own dedicated archive button.
  const canDeleteDemand = demand.status === 'OFFERED' || demand.status === 'INTERESTED_AVAILABLE'
  const canArchiveDemand = isPublished && !demand.isArchived && !isExpiredWithoutAssignment(demand)

  // SIB-924, option 3: editing is locked as soon as an expression of interest
  // exists. With a backend that reports hasInterestHistory, published demands
  // without interest history are editable; an older backend (flag absent)
  // still rejects every published-demand edit, so the edit stays locked there
  // with an honest reason instead of a save that can only fail.
  const interestHistoryKnown = typeof demand.hasInterestHistory === 'boolean'
  const demandHasInterestEvidence = hasInterestEvidence(demand, interestedSibs)
  const canEditDemand =
    !demand.isArchived && !demandHasInterestEvidence && (interestHistoryKnown || !isPublished)
  const editLockReason = demandHasInterestEvidence
    ? 'Kann nicht mehr bearbeitet werden — es liegen bereits Interessenbekundungen vor.'
    : demand.isArchived
      ? 'Ein archivierter Bedarf kann nicht bearbeitet werden.'
      : !interestHistoryKnown && isPublished
        ? 'Ein ausgeschriebener Bedarf kann derzeit nicht bearbeitet werden.'
        : null

  return (
    <Box bg="bg" minH="100vh" px={8} py={6}>
      <VStack align="stretch" gap={6} w="full" maxW="7xl" mx="auto" pb={24}>
        <Button
          alignSelf="start"
          variant="outline"
          onClick={() => {
            if (location.state?.returnToUnionMarketplace) {
              navigate(UNION_MANAGER_ROUTES.marketplace, { replace: true })
              return
            }

            navigate(-1)
          }}
        >
          <LuChevronLeft />
          Zurück
        </Button>

        <HStack gap={3}>
          <Heading size="xl">{getDemandName(demand)}</Heading>
          {statusBadge(demand)}
        </HStack>

        <Tabs.Root value={activeTab} onValueChange={details => setActiveTab(details.value)}>
          <Tabs.List>
            <Tabs.Trigger
              value="retro"
              disabled={
                (demand.allocationsCount ?? 0) === 0 &&
                demand.status !== 'PARTIALLY_COVERED' &&
                demand.status !== 'COVERED'
              }
            >
              Retro
            </Tabs.Trigger>
            <Tabs.Trigger
              value="matching"
              disabled={demand?.status === 'COVERED' || demand?.status === 'confirmed'}
            >
              Matching
            </Tabs.Trigger>
            <Tabs.Trigger value="interested" disabled={!hasInterestedSibs}>
              Interessenten
            </Tabs.Trigger>
            <Tabs.Trigger value="region">Region</Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value="retro">
            <Grid templateColumns={{ base: '1fr', xl: 'minmax(0, 1fr) 360px' }} gap={8} mt={4}>
              <Stack gap={5} minW={0}>
                <Text color="fg.muted">
                  Dieser Bedarf ist teilweise vermittelt. Die Übersicht zeigt die bereits
                  verteilten Leistungen und den offenen Rest.
                </Text>

                <Card.Root>
                  <Card.Body>
                    <Stack gap={6}>
                      <Text fontWeight="semibold">Outcome</Text>

                      <SimpleGrid columns={2}>
                        <Stack>
                          <Text color="fg.muted" fontSize="sm">
                            Vermittelte SIBs
                          </Text>

                          <Text fontSize="4xl" fontWeight="bold">
                            {demand.allocationsCount ?? 0}
                          </Text>

                          {demand.allocations?.map(allocation => (
                            <Text key={allocation.userId} color="fg.muted">
                              {allocation.userName}
                            </Text>
                          ))}
                        </Stack>

                        <Stack>
                          <Text color="fg.muted" fontSize="sm">
                            Offener Rest
                          </Text>

                          <SimpleGrid columns={4}>
                            <Stack>
                              <Text color="fg.muted" fontSize="sm">
                                TOJ
                              </Text>
                              <Text fontWeight="bold">
                                {getRemainingDemandUnits(demand, 'TOJ')} TOJ
                              </Text>
                            </Stack>

                            <Stack>
                              <Text color="fg.muted" fontSize="sm">
                                Beratung
                              </Text>
                              <Text fontWeight="bold">
                                {getRemainingDemandUnits(demand, 'CONSULTING')} Std.
                              </Text>
                            </Stack>

                            <Stack>
                              <Text color="fg.muted" fontSize="sm">
                                Training
                              </Text>
                              <Text fontWeight="bold">
                                {getRemainingDemandUnits(demand, 'TRAINING')} Tr.
                              </Text>
                            </Stack>

                            <Stack>
                              <Text color="fg.muted" fontSize="sm">
                                Pilot
                              </Text>
                              <Text fontWeight="bold">
                                {getRemainingDemandUnits(demand, 'PILOT')} Stk.
                              </Text>
                            </Stack>
                          </SimpleGrid>
                        </Stack>
                      </SimpleGrid>

                      <Separator />

                      <Stack gap={4}>
                        <Text fontWeight="semibold">Verteilte Leistungen</Text>

                        {demand.allocations?.map(allocation => (
                          <Card.Root key={allocation.userId} borderColor="border.muted">
                            <Card.Body>
                              <Stack gap={4}>
                                <Text fontWeight="bold">{allocation.userName}</Text>

                                <SimpleGrid columns={4}>
                                  <Stack>
                                    <Text color="fg.muted" fontSize="sm">
                                      TOJ
                                    </Text>
                                    <Text fontSize="2xl" fontWeight="bold">
                                      {getAllocationUnits(allocation, 'TOJ')} TOJ
                                    </Text>
                                  </Stack>

                                  <Stack>
                                    <Text color="fg.muted" fontSize="sm">
                                      Beratung
                                    </Text>
                                    <Text fontSize="2xl" fontWeight="bold">
                                      {getAllocationUnits(allocation, 'CONSULTING')} Std.
                                    </Text>
                                  </Stack>

                                  <Stack>
                                    <Text color="fg.muted" fontSize="sm">
                                      Training
                                    </Text>
                                    <Text fontSize="2xl" fontWeight="bold">
                                      {getAllocationUnits(allocation, 'TRAINING')} Tr.
                                    </Text>
                                  </Stack>

                                  <Stack>
                                    <Text color="fg.muted" fontSize="sm">
                                      Pilot
                                    </Text>
                                    <Text fontSize="2xl" fontWeight="bold">
                                      {getAllocationUnits(allocation, 'PILOT')} Stk.
                                    </Text>
                                  </Stack>
                                </SimpleGrid>
                              </Stack>
                            </Card.Body>
                          </Card.Root>
                        ))}
                      </Stack>

                      <Separator />

                      <Stack gap={2}>
                        <Text fontWeight="semibold">Zwischenstand</Text>
                        {statusBadge(demand)}
                      </Stack>
                    </Stack>
                  </Card.Body>
                </Card.Root>
              </Stack>

              <Card.Root alignSelf="start">
                <Card.Body>
                  <Stack gap={6}>
                    <Stack gap={3}>
                      <Heading size="md">Offener Rest</Heading>

                      <SimpleGrid columns={4}>
                        {[
                          { type: 'TOJ', label: 'TOJ', unit: 'TOJ' },
                          { type: 'CONSULTING', label: 'Beratung', unit: 'Std.' },
                          { type: 'TRAINING', label: 'Training', unit: 'Tr.' },
                          { type: 'PILOT', label: 'Pilot', unit: 'Stk.' },
                        ].map(service => {
                          const remaining = getRemainingDemandUnits(demand, service.type)
                          const assigned = getAllocatedDemandUnits(demand, service.type)

                          return (
                            <Stack key={service.type} gap={0}>
                              <Text color="fg.muted" fontSize="sm">
                                {service.label}
                              </Text>

                              <HStack>
                                <Text fontSize="2xl" fontWeight="bold">
                                  {remaining}
                                </Text>

                                {assigned > 0 && (
                                  <Badge colorPalette="green" variant="subtle" borderRadius="full">
                                    -{assigned}
                                  </Badge>
                                )}
                              </HStack>

                              <Text fontSize="sm">{service.unit}</Text>
                            </Stack>
                          )
                        })}
                      </SimpleGrid>
                    </Stack>

                    <Separator />

                    <Stack gap={4}>
                      <Heading size="md">Bedarfskontext</Heading>

                      {/* <SimpleGrid columns={2} gap={4}> */}
                      <Grid templateColumns="2fr 1fr" gap={4}>
                        <Stack gap={1}>
                          <Text color="fg.muted" fontSize="sm">
                            Leistungsempfänger
                          </Text>
                          <Text fontWeight="bold">{getDemandName(demand)}</Text>
                        </Stack>

                        <Stack gap={1}>
                          <Text color="fg.muted" fontSize="sm">
                            PLZ / Ort
                          </Text>
                          <Text fontWeight="bold">
                            {demand.postal || '—'} {demand.city || ''}
                          </Text>
                        </Stack>

                        <Stack gap={1}>
                          <Text color="fg.muted" fontSize="sm">
                            Vxx
                          </Text>
                          <Text fontWeight="bold">{demand.salesRegion}</Text>
                        </Stack>

                        <Stack gap={1}>
                          <Text color="fg.muted" fontSize="sm">
                            Sichtbarkeit
                          </Text>
                          <Text fontWeight="bold">
                            {demand.visibilityMode === 'ALL_USERS'
                              ? 'Alle SIB'
                              : 'Ausgewählte SIB'}
                          </Text>
                        </Stack>
                      </Grid>

                      <Stack gap={1}>
                        <Text color="fg.muted" fontSize="sm">
                          Zeitraum
                        </Text>
                        <Text fontWeight="bold">
                          {formatGermanDate(demand.periodStart)} –{' '}
                          {formatGermanDate(demand.periodEnd)}
                        </Text>
                      </Stack>

                      <Stack gap={2}>
                        <Text color="fg.muted" fontSize="sm">
                          Themen
                        </Text>
                        <HStack wrap="wrap" gap={2} align="start">
                          {demand.topic ? (
                            demand.topic.split(',').map(topic => {
                              const code = topic.trim()

                              return (
                                <Badge
                                  key={code}
                                  colorPalette="gray"
                                  variant="subtle"
                                  borderRadius="md"
                                  whiteSpace="normal"
                                  maxW="full"
                                >
                                  {getTopicLabel(code)}
                                </Badge>
                              )
                            })
                          ) : (
                            <Text color="fg.muted" fontSize="sm">
                              —
                            </Text>
                          )}
                        </HStack>
                      </Stack>
                    </Stack>

                    <Separator />

                    <Stack gap={4}>
                      <Heading size="md">Rückmeldungen</Heading>

                      <SimpleGrid columns={2}>
                        <Stack gap={1}>
                          <Text color="fg.muted">Ablehnungen</Text>
                          <Text fontSize="3xl" fontWeight="bold">
                            {demand.rejectedCount ?? 0}
                          </Text>
                          <Button size="sm" variant="outline" disabled>
                            Gründe
                          </Button>
                        </Stack>

                        <Stack gap={1}>
                          <Text color="fg.muted">Interessenten</Text>
                          <Text fontSize="3xl" fontWeight="bold">
                            {demand.interestsCount ?? 0}
                          </Text>
                        </Stack>
                      </SimpleGrid>
                    </Stack>
                  </Stack>
                </Card.Body>
              </Card.Root>
            </Grid>
          </Tabs.Content>

          <Tabs.Content value="matching">
            <Text color="fg.muted" mt={4} mb={5}>
              Hier sehen Sie alle SIB, die nach Region, Themen und verfügbarer Kapazität zum Bedarf
              passen.
            </Text>

            <Grid templateColumns={{ base: '1fr', xl: '2fr 1fr' }} gap={8}>
              <Stack gap={4}>
                {matchingSibs.length === 0 ? (
                  <Text color="fg.muted">Keine passenden SIB gefunden.</Text>
                ) : (
                  matchingSibs.map(sib => (
                    <MatchingSibCard
                      key={sib.id}
                      sib={sib}
                      demand={demand}
                      checked={selectedSibIds.includes(sib.id)}
                      onChange={checked => toggleSib(sib.id, checked)}
                    />
                  ))
                )}
              </Stack>

              <Card.Root alignSelf="start">
                <Card.Body>
                  <Stack gap={6}>
                    <Stack gap={3}>
                      <Heading size="md">Leistungsumfang</Heading>

                      <SimpleGrid columns={4}>
                        {[
                          { type: 'TOJ', label: 'TOJ', unit: 'TOJ' },
                          { type: 'CONSULTING', label: 'Beratung', unit: 'Std.' },
                          { type: 'TRAINING', label: 'Training', unit: 'Tr.' },
                          { type: 'PILOT', label: 'Pilot', unit: 'Stk.' },
                        ].map(service => {
                          const remaining = getRemainingUnitsFromAssignments(
                            demand,
                            assignments,
                            service.type
                          )
                          const assigned = getAssignedUnits(assignments, service.type)

                          return (
                            <Stack key={service.type} gap={0}>
                              <Text color="fg.muted" fontSize="sm">
                                {service.label}
                              </Text>

                              <HStack>
                                <Text fontSize="2xl" fontWeight="bold">
                                  {remaining}
                                </Text>

                                {assigned > 0 && (
                                  <Badge colorPalette="green" variant="subtle" borderRadius="full">
                                    -{assigned}
                                  </Badge>
                                )}
                              </HStack>

                              <Text fontSize="sm">{service.unit}</Text>
                            </Stack>
                          )
                        })}
                      </SimpleGrid>
                    </Stack>

                    <Separator />

                    <Stack gap={4}>
                      <Heading size="md">Bedarfskontext</Heading>

                      {/* <SimpleGrid columns={2} gap={4}> */}
                      <Grid templateColumns="2fr 1fr" gap={4}>
                        <Stack gap={1}>
                          <Text color="fg.muted" fontSize="sm">
                            Leistungsempfänger
                          </Text>
                          <Text fontWeight="bold">{getDemandName(demand)}</Text>
                        </Stack>

                        <Stack gap={1}>
                          <Text color="fg.muted" fontSize="sm">
                            PLZ / Ort
                          </Text>
                          <Text fontWeight="bold">
                            {demand.postal || '—'} {demand.city || ''}
                          </Text>
                        </Stack>

                        <Stack gap={1}>
                          <Text color="fg.muted" fontSize="sm">
                            Vxx
                          </Text>
                          <Text fontWeight="bold">{demand.salesRegion}</Text>
                        </Stack>

                        <Stack gap={1}>
                          <Text color="fg.muted" fontSize="sm">
                            Sichtbarkeit
                          </Text>
                          <Text fontWeight="bold">
                            {!demand.published
                              ? 'Entwurf'
                              : demand.visibilityMode === 'ALL_USERS'
                                ? 'Alle SIB'
                                : 'Auswahl'}
                          </Text>
                        </Stack>
                      </Grid>

                      <Stack gap={1}>
                        <Text color="fg.muted" fontSize="sm">
                          Zeitraum
                        </Text>
                        <Text>
                          {formatGermanDate(demand.periodStart)} –{' '}
                          {formatGermanDate(demand.periodEnd)}
                        </Text>
                      </Stack>

                      <Stack gap={2}>
                        <Text color="fg.muted" fontSize="sm">
                          Themen
                        </Text>
                        <HStack wrap="wrap" gap={2} align="start">
                          {demand.topic ? (
                            demand.topic.split(',').map(topic => {
                              const code = topic.trim()

                              return (
                                <Badge
                                  key={code}
                                  colorPalette="gray"
                                  variant="subtle"
                                  borderRadius="md"
                                  whiteSpace="normal"
                                  maxW="full"
                                >
                                  {getTopicLabel(code)}
                                </Badge>
                              )
                            })
                          ) : (
                            <Text color="fg.muted" fontSize="sm">
                              —
                            </Text>
                          )}
                        </HStack>
                      </Stack>
                    </Stack>

                    <Separator />

                    <Stack gap={4}>
                      <Heading size="md">Rückmeldungen</Heading>

                      <SimpleGrid columns={2}>
                        <Stack gap={1}>
                          <Text color="fg.muted">Ablehnungen</Text>
                          <Text fontSize="3xl" fontWeight="bold">
                            {demand.rejectedCount ?? 0}
                          </Text>
                          <Button size="sm" variant="outline" disabled>
                            Gründe
                          </Button>
                        </Stack>

                        <Stack gap={1}>
                          <Text color="fg.muted">Interessenten</Text>
                          <Text fontSize="3xl" fontWeight="bold">
                            {demand.interestsCount ?? 0}
                          </Text>
                        </Stack>
                      </SimpleGrid>
                    </Stack>
                  </Stack>
                </Card.Body>
              </Card.Root>
            </Grid>
          </Tabs.Content>
          <Tabs.Content value="interested">
            <Text color="fg.muted" mt={4} mb={5}>
              Hier sehen Sie alle SIB, die Interesse an dieser Ausschreibung gemeldet haben.
            </Text>

            <Grid
              templateColumns={{ base: '1fr', xl: 'minmax(0, 1fr) 360px' }}
              gap={8}
              alignItems="start"
            >
              <Stack gap={4} minW={0}>
                {interestedSibs.length === 0 ? (
                  <Text color="fg.muted">Noch keine interessierten SIB vorhanden.</Text>
                ) : (
                  interestedSibs.map(sib => (
                    <InterestedSibCard
                      key={sib.id}
                      sib={sib}
                      checked={selectedInterestedSibIds.includes(sib.id)}
                      onChange={checked => toggleInterestedSib(sib.id, checked)}
                    />
                  ))
                )}
              </Stack>

              <Card.Root alignSelf="start">
                <Card.Body>
                  <Stack gap={6}>
                    <Stack gap={3}>
                      <Heading size="md">
                        {hasAnyAssignment(assignments) ? 'Offener Restbedarf' : 'Leistungsumfang'}
                      </Heading>

                      <SimpleGrid columns={4}>
                        {[
                          { type: 'TOJ', label: 'TOJ', unit: 'TOJ' },
                          { type: 'CONSULTING', label: 'Beratung', unit: 'Std.' },
                          { type: 'TRAINING', label: 'Training', unit: 'Tr.' },
                          { type: 'PILOT', label: 'Pilot', unit: 'Stk.' },
                        ].map(service => {
                          const remaining = getRemainingUnitsFromAssignments(
                            demand,
                            assignments,
                            service.type
                          )
                          const assigned = getAssignedUnits(assignments, service.type)

                          return (
                            <Stack key={service.type} gap={0}>
                              <Text color="fg.muted" fontSize="sm">
                                {service.label}
                              </Text>

                              <HStack>
                                <Text fontSize="2xl" fontWeight="bold">
                                  {remaining}
                                </Text>

                                {assigned > 0 && (
                                  <Badge colorPalette="green" variant="subtle">
                                    -{assigned}
                                  </Badge>
                                )}
                              </HStack>

                              <Text fontSize="sm">{service.unit}</Text>
                            </Stack>
                          )
                        })}
                      </SimpleGrid>
                    </Stack>

                    <Separator />

                    <Stack gap={4}>
                      <Heading size="md">Bedarfskontext</Heading>

                      {/* <SimpleGrid columns={2} gap={4}> */}
                      <Grid templateColumns="2fr 1fr" gap={4}>
                        <Stack gap={1}>
                          <Text color="fg.muted" fontSize="sm">
                            Leistungsempfänger
                          </Text>
                          <Text fontWeight="bold">{getDemandName(demand)}</Text>
                        </Stack>

                        <Stack gap={1}>
                          <Text color="fg.muted" fontSize="sm">
                            PLZ / Ort
                          </Text>
                          <Text fontWeight="bold">
                            {demand.postal || '—'} {demand.city || ''}
                          </Text>
                        </Stack>

                        <Stack gap={1}>
                          <Text color="fg.muted" fontSize="sm">
                            Vxx
                          </Text>
                          <Text fontWeight="bold">{demand.salesRegion}</Text>
                        </Stack>

                        <Stack gap={1}>
                          <Text color="fg.muted" fontSize="sm">
                            Sichtbarkeit
                          </Text>
                          <Text fontWeight="bold">
                            {!demand.published
                              ? 'Entwurf'
                              : demand.visibilityMode === 'ALL_USERS'
                                ? 'Alle SIB'
                                : 'Auswahl'}
                          </Text>
                        </Stack>
                      </Grid>

                      <Stack gap={1}>
                        <Text color="fg.muted" fontSize="sm">
                          Zeitraum
                        </Text>
                        <Text>
                          {formatGermanDate(demand.periodStart)} –{' '}
                          {formatGermanDate(demand.periodEnd)}
                        </Text>
                      </Stack>

                      <Stack gap={2}>
                        <Text color="fg.muted" fontSize="sm">
                          Themen
                        </Text>
                        <HStack>
                          {(demand.topic?.split(',') ?? ['—']).map(topic => (
                            <Text key={topic.trim()} fontWeight="bold" fontSize="sm">
                              {topic.trim()}
                            </Text>
                          ))}
                        </HStack>
                      </Stack>
                    </Stack>

                    <Separator />

                    <Stack gap={4}>
                      <Heading size="md">Rückmeldungen</Heading>

                      <SimpleGrid columns={2}>
                        <Stack gap={1}>
                          <Text color="fg.muted">Ablehnungen</Text>
                          <Text fontSize="3xl" fontWeight="bold">
                            {demand.rejectedCount ?? 0}
                          </Text>
                          <Button size="sm" variant="outline" disabled>
                            Gründe
                          </Button>
                        </Stack>

                        <Stack gap={1}>
                          <Text color="fg.muted">Interessenten</Text>
                          <Text fontSize="3xl" fontWeight="bold">
                            {demand.interestsCount ?? 0}
                          </Text>
                        </Stack>
                      </SimpleGrid>
                    </Stack>
                  </Stack>
                </Card.Body>
              </Card.Root>
            </Grid>
          </Tabs.Content>
          <Tabs.Content value="region">
            <Text color="fg.muted" mt={4} mb={5}>
              Hier sehen Sie dieselben passenden SIB wie im Matching, gruppiert nach ihrer
              Vertriebsregion.
            </Text>

            {regionGroups.length === 0 ? (
              <Text color="fg.muted">Keine passenden SIB gefunden.</Text>
            ) : (
              <Stack gap={8}>
                {regionGroups.map(group => (
                  <Stack key={group.region} gap={4} data-testid="region-group">
                    <HStack gap={3}>
                      <Heading size="md">{group.region}</Heading>
                      <Badge variant="subtle">{group.sibs.length}</Badge>
                    </HStack>

                    {group.sibs.map(sib => (
                      <MatchingSibCard
                        key={sib.id}
                        sib={sib}
                        demand={demand}
                        checked={selectedSibIds.includes(sib.id)}
                        onChange={checked => toggleSib(sib.id, checked)}
                      />
                    ))}
                  </Stack>
                ))}
              </Stack>
            )}
          </Tabs.Content>
        </Tabs.Root>
      </VStack>

      {((isDemandActionable(demand.status) && activeTab !== 'retro') ||
        isExpiredWithoutAssignment(demand) ||
        (activeTab === 'retro' &&
          (demand.status === 'PARTIALLY_COVERED' ||
            demand.status === 'COVERED' ||
            demand.status === 'partially_confirmed' ||
            demand.status === 'confirmed'))) && (
        <Box
          position="fixed"
          left="50%"
          bottom={6}
          transform="translateX(-50%)"
          bg="bg"
          borderWidth="1px"
          borderColor="border.muted"
          borderRadius="xl"
          shadow="lg"
          px={4}
          py={3}
          zIndex={10}
        >
          <HStack>
            {isDemandActionable(demand.status) && activeTab !== 'retro' && (
              <>
                {activeTab === 'interested' ? (
                  <>
                    <Button
                      disabled={selectedInterestedSibIds.length === 0}
                      onClick={() => setShowAssignDrawer(true)}
                    >
                      Zuschlag geben
                    </Button>

                    <Button
                      colorPalette="red"
                      variant="subtle"
                      disabled={selectedInterestedSibIds.length === 0}
                      loading={isRejectingInterested}
                      onClick={handleRejectSelectedInterestedSibs}
                    >
                      Ablehnen
                    </Button>

                    <Button
                      colorPalette="red"
                      loading={isMarkingNotMatchable}
                      onClick={handleMarkNotMatchable}
                    >
                      Nicht vermittelbar
                    </Button>
                  </>
                ) : (
                  <>
                    <Tooltip
                      content="Ein ausgeschriebener Bedarf kann nicht aktualisiert werden."
                      disabled={!isPublished}
                    >
                      <Button
                        disabled={isPublished || selectedSibIds.length === 0}
                        loading={isPublishingSelected}
                        onClick={handlePublishSelected}
                      >
                        Auswahl ausschreiben
                      </Button>
                    </Tooltip>

                    <Tooltip
                      content="Ein ausgeschriebener Bedarf kann nicht aktualisiert werden."
                      disabled={!isPublished}
                    >
                      <Button
                        disabled={isPublished}
                        loading={isPublishingAll}
                        onClick={handlePublishAll}
                      >
                        Alle SIB ausschreiben
                      </Button>
                    </Tooltip>
                  </>
                )}

                {/* SIB-924: the edit action is always visible while the demand is
                    actionable, and a locked demand explains WHY it cannot be
                    edited any more instead of hiding the action. */}
                <Separator orientation="vertical" h="24px" />

                <Tooltip content={editLockReason ?? ''} disabled={!editLockReason}>
                  {/* Not natively disabled: a disabled button emits no hover/
                      focus/tap events, so the tooltip (and the mobile tap
                      popover) explaining the lock would never open. data-disabled
                      keeps the Chakra disabled styling, the click guard keeps it
                      non-actionable. */}
                  <Button
                    variant="outline"
                    aria-disabled={!canEditDemand}
                    data-disabled={canEditDemand ? undefined : ''}
                    onClick={() => {
                      if (canEditDemand) setShowEditDrawer(true)
                    }}
                  >
                    Bedarf bearbeiten
                  </Button>
                </Tooltip>

                {(canDeleteDemand || canArchiveDemand) && (
                  <>
                    <Separator orientation="vertical" h="24px" />

                    {canDeleteDemand && (
                      <Button
                        colorPalette="red"
                        variant="outline"
                        onClick={() => setConfirmAction('delete')}
                      >
                        Bedarf löschen
                      </Button>
                    )}

                    {canArchiveDemand && (
                      <Button
                        colorPalette="red"
                        variant="outline"
                        onClick={() => setConfirmAction('archive')}
                      >
                        Bedarf archivieren
                      </Button>
                    )}
                  </>
                )}
              </>
            )}

            {((activeTab === 'retro' &&
              (demand.status === 'PARTIALLY_COVERED' ||
                demand.status === 'COVERED' ||
                demand.status === 'partially_confirmed' ||
                demand.status === 'confirmed')) ||
              isExpiredWithoutAssignment(demand)) && (
              <Button colorPalette="red" onClick={() => setConfirmAction('archive')}>
                Bedarf archivieren
              </Button>
            )}
          </HStack>
        </Box>
      )}
      {demand && (
        <AssignDemandDrawer
          open={showAssignDrawer}
          demand={demand}
          sibs={interestedSibs.filter(sib => selectedInterestedSibIds.includes(sib.id))}
          isSubmitting={isAssigning}
          onOpenChange={setShowAssignDrawer}
          onSubmit={handleAssignDemand}
        />
      )}
      <CreateDemandDrawer
        open={showEditDrawer}
        demand={demand}
        isSubmitting={isUpdating}
        onOpenChange={setShowEditDrawer}
        onSubmit={handleUpdateDemand}
      />

      {/* Destructive actions ask for confirmation first (SIB-924), following
          the established dialog pattern (see DeleteActivityModal). */}
      <Dialog.Root
        lazyMount
        open={confirmAction !== null}
        onOpenChange={({ open }) => {
          if (!open && !isRemoving) setConfirmAction(null)
        }}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>
                  {confirmAction === 'delete' ? 'Bedarf löschen' : 'Bedarf archivieren'}
                </Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                {confirmAction === 'delete'
                  ? 'Möchten Sie diesen Bedarf endgültig löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.'
                  : 'Möchten Sie diesen Bedarf archivieren? Er ist danach für SIBs nicht mehr sichtbar.'}
              </Dialog.Body>
              <Dialog.Footer>
                <Dialog.ActionTrigger asChild>
                  <Button colorPalette="gray" variant="outline" disabled={isRemoving}>
                    Abbrechen
                  </Button>
                </Dialog.ActionTrigger>
                <Button
                  variant="solid"
                  colorPalette="red"
                  loading={isRemoving}
                  onClick={confirmAction === 'delete' ? handleDeleteDemand : handleArchiveDemand}
                >
                  {confirmAction === 'delete' ? 'Bedarf löschen' : 'Bedarf archivieren'}
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Box>
  )
}
